import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BulkImportModal from '../components/BulkImportModal';
import { Building2, Plus, Edit3, Trash2, CheckCircle, AlertCircle, Phone, Mail, UserCheck, Sparkles, MapPin, X, FileSpreadsheet } from 'lucide-react';

export default function CompanyManagement() {
    const [companies, setCompanies] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [modalError, setModalError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const [companySectors, setCompanySectors] = useState([]);
    const [customSectorInput, setCustomSectorInput] = useState('');
    const [formData, setFormData] = useState({
        company_name: '',
        office_location: '',
        openings: '',
        contact_person_name: '',
        contact_person_number: '',
        contact_person_email: '',
        company_phone: '',
        company_email: '',
    });

    const fetchData = async () => {
        setLoading(true);
        setPageError('');
        try {
            const [cRes, sRes] = await Promise.all([
                axios.get('/api/companies'),
                axios.get('/api/sectors')
            ]);
            if (cRes.data.success) setCompanies(cRes.data.companies);
            if (sRes.data.success) setSectors(sRes.data.sectors);
        } catch (err) {
            setPageError(err.response?.data?.message || 'Failed to load companies.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const toggleSector = (sec) => {
        if (companySectors.includes(sec)) {
            setCompanySectors(companySectors.filter(s => s !== sec));
        } else {
            setCompanySectors([...companySectors, sec]);
        }
    };

    const handleAddCustomSector = () => {
        const trimmed = customSectorInput.trim();
        if (trimmed && !companySectors.includes(trimmed)) {
            setCompanySectors([...companySectors, trimmed]);
            if (!sectors.includes(trimmed)) {
                setSectors([...sectors, trimmed]);
            }
            setCustomSectorInput('');
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const openAddModal = () => {
        setEditId(null);
        setCompanySectors([]);
        setFormData({
            company_name: '',
            office_location: '',
            openings: '',
            contact_person_name: '',
            contact_person_number: '',
            contact_person_email: '',
            company_phone: '',
            company_email: '',
        });
        setModalError('');
        setFieldErrors({});
        setShowModal(true);
    };

    const openEditModal = (c) => {
        setEditId(c.id);
        setCompanySectors(c.sector ? c.sector.split(',').map(s => s.trim()) : []);
        setFormData({
            company_name: c.company_name || '',
            office_location: c.office_location || '',
            openings: c.openings || '',
            contact_person_name: c.contact_person_name || '',
            contact_person_number: c.contact_person_number || '',
            contact_person_email: c.contact_person_email || '',
            company_phone: c.company_phone || '',
            company_email: c.company_email || '',
        });
        setModalError('');
        setFieldErrors({});
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setModalError('');
        setFieldErrors({});
        setSubmitting(true);

        try {
            const payload = {
                ...formData,
                sector: companySectors.join(', '),
            };

            if (editId) {
                const res = await axios.put(`/api/companies/${editId}`, payload);
                if (res.data.success) {
                    setSuccessMsg('Company updated successfully!');
                    setShowModal(false);
                    fetchData();
                }
            } else {
                const res = await axios.post('/api/companies', payload);
                if (res.data.success) {
                    setSuccessMsg('Company added successfully!');
                    setShowModal(false);
                    fetchData();
                }
            }
        } catch (err) {
            if (err.response?.status === 422 && err.response?.data?.errors) {
                setFieldErrors(err.response.data.errors);
                setModalError(err.response.data.message || 'Validation error. Please check the fields below.');
            } else {
                setModalError(err.response?.data?.message || 'Operation failed. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this company entry?')) return;
        try {
            const res = await axios.delete(`/api/companies/${id}`);
            if (res.data.success) {
                setSuccessMsg('Company deleted.');
                fetchData();
            }
        } catch (err) {
            setPageError(err.response?.data?.message || 'Delete failed.');
        }
    };

    const renderFieldError = (field) => {
        if (!fieldErrors[field]) return null;
        const msg = Array.isArray(fieldErrors[field]) ? fieldErrors[field][0] : fieldErrors[field];
        return (
            <p className="text-red-400 text-xs font-semibold mt-1 flex items-center space-x-1">
                <span>⚠️ {msg}</span>
            </p>
        );
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
            
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-purple-400 bg-purple-500/10 px-3 py-1 rounded-full border border-purple-500/20">
                        Employer Stalls
                    </span>
                    <h1 className="text-3xl font-extrabold mt-2 bg-gradient-to-r from-white via-slate-100 to-purple-300 bg-clip-text text-transparent">
                        Company Registry
                    </h1>
                    <p className="text-sm text-slate-400">Participating employers, sector tags, contact details & selection counts</p>
                </div>

                <button
                    onClick={openAddModal}
                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-sm shadow-lg shadow-purple-600/30 flex items-center space-x-2 transition-all"
                >
                    <Plus className="w-4 h-4" />
                    <span>Register New Company</span>
                </button>
            </div>

            {/* Page Level Notifications */}
            {successMsg && (
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                        <span>{successMsg}</span>
                    </div>
                    <button onClick={() => setSuccessMsg('')} className="text-xs text-slate-400 hover:text-white font-bold">Dismiss</button>
                </div>
            )}

            {pageError && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                        <span>{pageError}</span>
                    </div>
                    <button onClick={() => setPageError('')} className="text-xs text-slate-400 hover:text-white font-bold">Dismiss</button>
                </div>
            )}

            {/* Companies Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {companies.map(comp => (
                    <div key={comp.id} className="glass-card rounded-2xl p-6 border border-slate-800 flex flex-col justify-between hover:border-purple-500/40 transition-all group">
                        <div className="space-y-4">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="font-extrabold text-lg text-white group-hover:text-purple-300 transition-colors">
                                        {comp.company_name}
                                    </div>
                                    <div className="text-xs text-slate-400 flex items-center space-x-1 mt-1">
                                        <MapPin className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                                        <span>{comp.office_location || 'Indore'}</span>
                                    </div>
                                </div>

                                <div className="text-right">
                                    <span className="text-xs font-black text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-lg">
                                        {comp.selected_count || 0} Selected
                                    </span>
                                </div>
                            </div>

                            {/* Openings */}
                            {comp.openings && (
                                <div className="text-xs font-semibold text-indigo-300 bg-indigo-950/60 p-2.5 rounded-xl border border-indigo-500/20">
                                    💼 {comp.openings}
                                </div>
                            )}

                            {/* Sectors */}
                            <div>
                                <div className="text-[10px] uppercase font-bold text-slate-400 mb-1">Operating Sectors</div>
                                <div className="flex flex-wrap gap-1">
                                    {comp.sector ? comp.sector.split(',').map((s, idx) => (
                                        <span key={idx} className="text-[11px] font-medium bg-slate-900 text-slate-300 px-2 py-0.5 rounded border border-slate-800">
                                            {s.trim()}
                                        </span>
                                    )) : <span className="text-xs text-slate-500">General</span>}
                                </div>
                            </div>

                            {/* HR / Contact Details */}
                            <div className="text-xs text-slate-300 space-y-1 pt-2 border-t border-slate-800">
                                {comp.contact_person_name && (
                                    <div>👤 Contact Person: <span className="font-bold text-white">{comp.contact_person_name}</span></div>
                                )}
                                {comp.contact_person_number && (
                                    <div>📱 Phone: <span className="text-slate-400">{comp.contact_person_number}</span></div>
                                )}
                                {comp.contact_person_email && (
                                    <div>✉️ Email: <span className="text-slate-400">{comp.contact_person_email}</span></div>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-end space-x-2 pt-4 border-t border-slate-800/80 mt-4">
                            <button
                                onClick={() => openEditModal(comp)}
                                className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
                            >
                                <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => handleDelete(comp.id)}
                                className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
                    <div className="w-full max-w-lg glass-card rounded-3xl p-6 sm:p-8 border border-slate-800 shadow-2xl relative max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4 border-b border-slate-800 pb-3">
                            <h2 className="text-xl font-bold text-white">
                                {editId ? 'Edit Company Information' : 'Register New Employer'}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Top Modal Error Banner */}
                        {modalError && (
                            <div className="mb-4 p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold flex items-center space-x-2">
                                <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                                <span>{modalError}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-300 mb-1">Company Name *</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.company_name}
                                    onChange={e => handleInputChange('company_name', e.target.value)}
                                    className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                        fieldErrors.company_name ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                    }`}
                                    placeholder="TechSolutions India Pvt Ltd"
                                />
                                {renderFieldError('company_name')}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 mb-1">Office Location</label>
                                <input
                                    type="text"
                                    value={formData.office_location}
                                    onChange={e => handleInputChange('office_location', e.target.value)}
                                    className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                        fieldErrors.office_location ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                    }`}
                                    placeholder="Indore IT Park"
                                />
                                {renderFieldError('office_location')}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-300 mb-1">Vacancies / Openings</label>
                                <input
                                    type="text"
                                    value={formData.openings}
                                    onChange={e => handleInputChange('openings', e.target.value)}
                                    className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                        fieldErrors.openings ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                    }`}
                                    placeholder="20 Positions (Web Dev, QA, Sales)"
                                />
                                {renderFieldError('openings')}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-purple-300 mb-1">Operating Sectors (Select or add custom sector)</label>
                                <div className="flex flex-wrap gap-1.5 p-3 bg-slate-900 rounded-xl border border-slate-800">
                                    {sectors.map(sec => {
                                        const isSel = companySectors.includes(sec);
                                        return (
                                            <button
                                                type="button"
                                                key={sec}
                                                onClick={() => toggleSector(sec)}
                                                className={`text-xs px-2.5 py-1 rounded border transition-all ${
                                                    isSel
                                                        ? 'bg-purple-600 text-white border-purple-500 font-bold'
                                                        : 'bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700'
                                                }`}
                                            >
                                                {isSel ? '✓ ' : '+ '}{sec}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Custom Sector Input */}
                                <div className="mt-2.5 flex items-center space-x-2">
                                    <input
                                        type="text"
                                        value={customSectorInput}
                                        onChange={e => setCustomSectorInput(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomSector(); } }}
                                        placeholder="Type custom sector (e.g. Solar Energy, R&D)..."
                                        className="flex-1 px-3.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:border-purple-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddCustomSector}
                                        className="px-3 py-1.5 rounded-lg bg-purple-700 hover:bg-purple-600 text-white text-xs font-bold shrink-0 shadow-sm"
                                    >
                                        + Add Sector
                                    </button>
                                </div>
                                {renderFieldError('sector')}
                            </div>

                            <div className="p-3 bg-purple-950/40 border border-purple-500/20 rounded-xl text-xs text-purple-200">
                                💡 <strong>Automatic HR Account:</strong> Entering Contact Person details will automatically create a <strong>Company HR</strong> login account with default password <code>123456</code>.
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Contact Person Name</label>
                                    <input
                                        type="text"
                                        placeholder="HR Manager Name"
                                        value={formData.contact_person_name}
                                        onChange={e => handleInputChange('contact_person_name', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.contact_person_name ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('contact_person_name')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Contact Person Mobile</label>
                                    <input
                                        type="tel"
                                        placeholder="9876543210"
                                        value={formData.contact_person_number}
                                        onChange={e => handleInputChange('contact_person_number', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.contact_person_number ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('contact_person_number')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Contact Person Email</label>
                                    <input
                                        type="email"
                                        placeholder="hr@company.com"
                                        value={formData.contact_person_email}
                                        onChange={e => handleInputChange('contact_person_email', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.contact_person_email ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('contact_person_email')}
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold shadow-lg shadow-purple-600/30"
                                >
                                    {submitting ? 'Saving...' : (editId ? 'Update Company' : 'Save Company')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
